import { BoundingBox, PnidEquipment, PnidNode, PnidInstrument, instrumentRegions } from '../types';

export interface Point {
  x: number;
  y: number;
}

export interface LineSegment {
  id: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  kind?: string;
  confidence?: number;
}

export interface MergedLineSegment extends Omit<LineSegment, 'id'> {
  /**
   * Stable representative ID for the merged segment (defaults to min(source ids)).
   * This is meant for UI identity (selection/highlight), not for persistence.
   */
  id: string;
  /** Original segment ids that were merged into this one. */
  source_segment_ids: string[];
  /** Number of raw segments merged. */
  merged_count: number;
  /** Max confidence among merged segments (if provided). */
  merged_confidence_max?: number;
}

export interface MergeLineSegmentsOptions {
  /** Max angular difference (degrees) to consider segments colinear. */
  angleThresholdDeg?: number;
  /** Max average perpendicular distance (px) to consider segments on same line. */
  distanceThresholdPx?: number;
  /** Minimum 1D overlap ratio (intersection / min length) along axis to merge. */
  overlapThreshold?: number;
  /** If endpoints are within this distance (px), allow merging even with low overlap. */
  endpointDistancePx?: number;
  /** Ignore segments shorter than this length (px) for merging; they return as-is. */
  minLengthPx?: number;
}


/**
 * Check if a point is inside a bounding box
 */
export function isPointInBbox(point: Point, bbox: BoundingBox): boolean {
  return (
    point.x >= bbox.x1 &&
    point.x <= bbox.x2 &&
    point.y >= bbox.y1 &&
    point.y <= bbox.y2
  );
}

/** The box's area in square sheet pixels. */
export function bboxArea(bbox: BoundingBox): number {
  return (bbox.x2 - bbox.x1) * (bbox.y2 - bbox.y1);
}

/**
 * Calculate distance from a point to a bounding box edge.
 * Returns 0 if point is inside the bbox.
 */
export function pointToBboxDistance(point: Point, bbox: BoundingBox): number {
  if (isPointInBbox(point, bbox)) return 0;

  // Find closest point on bbox boundary
  const closestX = Math.max(bbox.x1, Math.min(point.x, bbox.x2));
  const closestY = Math.max(bbox.y1, Math.min(point.y, bbox.y2));

  return Math.sqrt((point.x - closestX) ** 2 + (point.y - closestY) ** 2);
}

/**
 * Calculate distance from a point to a polyline (minimum distance to any segment)
 */
export function pointToPolylineDistance(point: Point, polyline: number[]): number {
  if (polyline.length < 4) return Infinity;

  let minDist = Infinity;

  for (let i = 0; i < polyline.length - 2; i += 2) {
    const x1 = polyline[i];
    const y1 = polyline[i + 1];
    const x2 = polyline[i + 2];
    const y2 = polyline[i + 3];

    const dist = pointToSegmentDistance(point, { x: x1, y: y1 }, { x: x2, y: y2 });
    minDist = Math.min(minDist, dist);
  }

  return minDist;
}

/**
 * Calculate distance from a point to a line segment
 */
function pointToSegmentDistance(point: Point, segStart: Point, segEnd: Point): number {
  const dx = segEnd.x - segStart.x;
  const dy = segEnd.y - segStart.y;
  const lengthSq = dx * dx + dy * dy;

  if (lengthSq === 0) {
    // Segment is a point
    return Math.sqrt((point.x - segStart.x) ** 2 + (point.y - segStart.y) ** 2);
  }

  // Project point onto line, clamped to segment
  let t = ((point.x - segStart.x) * dx + (point.y - segStart.y) * dy) / lengthSq;
  t = Math.max(0, Math.min(1, t));

  const projX = segStart.x + t * dx;
  const projY = segStart.y + t * dy;

  return Math.sqrt((point.x - projX) ** 2 + (point.y - projY) ** 2);
}

/**
 * Check if a line segment intersects a bounding box
 */
function segmentIntersectsBbox(
  x1: number, y1: number, x2: number, y2: number,
  bbox: BoundingBox
): boolean {
  // Check if either endpoint is inside bbox
  if ((x1 >= bbox.x1 && x1 <= bbox.x2 && y1 >= bbox.y1 && y1 <= bbox.y2) ||
      (x2 >= bbox.x1 && x2 <= bbox.x2 && y2 >= bbox.y1 && y2 <= bbox.y2)) {
    return true;
  }
  
  // Check if segment crosses any bbox edge using line-line intersection
  const bboxEdges = [
    [bbox.x1, bbox.y1, bbox.x2, bbox.y1], // top
    [bbox.x2, bbox.y1, bbox.x2, bbox.y2], // right
    [bbox.x1, bbox.y2, bbox.x2, bbox.y2], // bottom
    [bbox.x1, bbox.y1, bbox.x1, bbox.y2], // left
  ];
  
  for (const [ex1, ey1, ex2, ey2] of bboxEdges) {
    if (segmentsIntersect(x1, y1, x2, y2, ex1, ey1, ex2, ey2)) {
      return true;
    }
  }
  
  return false;
}

/**
 * Check if two line segments intersect
 */
function segmentsIntersect(
  x1: number, y1: number, x2: number, y2: number,
  x3: number, y3: number, x4: number, y4: number
): boolean {
  const denom = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);
  if (Math.abs(denom) < 1e-10) return false; // parallel
  
  const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denom;
  const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denom;
  
  return ua >= 0 && ua <= 1 && ub >= 0 && ub <= 1;
}

/**
 * Check if a polyline intersects (passes through) a bounding box
 */
export function polylineIntersectsBbox(polyline: number[], bbox: BoundingBox): boolean {
  if (polyline.length < 4) return false;
  
  for (let i = 0; i < polyline.length - 2; i += 2) {
    const x1 = polyline[i];
    const y1 = polyline[i + 1];
    const x2 = polyline[i + 2];
    const y2 = polyline[i + 3];
    
    if (segmentIntersectsBbox(x1, y1, x2, y2, bbox)) {
      return true;
    }
  }
  
  return false;
}

/** The bind margin on screen: a click this far outside a box still binds to it (Q16). */
export const BIND_MARGIN_SCREEN_PX = 12;

/** The marked geometries a line vertex can bind to. */
export interface BindTargets {
  equipments: PnidEquipment[];
  instruments: PnidInstrument[];
  nodes: PnidNode[];
}

/** The geometry the canvas lights while the pointer is over a target. */
export type EntityOutline =
  | { kind: 'box'; bbox: BoundingBox }
  | { kind: 'polyline'; points: number[] };

/** The entity under a click, the point on it the vertex snaps to, and its outline. */
export interface EntityHit {
  kind: 'equipment' | 'instrument' | 'node';
  id: string;
  snap: Point;
  outline: EntityOutline;
}

/**
 * The nearest point on a box's perimeter to `point`.
 *
 * DECISION(2026-10-22, abhishek): A bound vertex snaps to the nearest point on the
 * asset's box edge, from inside the box as well as from outside it (D3).
 * The nozzle sits on the box edge, and a click inside the box means the asset, not a
 * point inside it. SMEs who report a vertex that jumped to a box they did not mean are
 * the signal to shrink the margin or the snap.
 */
export function snapToBoxEdge(point: Point, bbox: BoundingBox): Point {
  if (!isPointInBbox(point, bbox)) {
    return {
      x: Math.max(bbox.x1, Math.min(point.x, bbox.x2)),
      y: Math.max(bbox.y1, Math.min(point.y, bbox.y2)),
    };
  }
  const toLeft = point.x - bbox.x1;
  const toRight = bbox.x2 - point.x;
  const toTop = point.y - bbox.y1;
  const toBottom = bbox.y2 - point.y;
  const nearest = Math.min(toLeft, toRight, toTop, toBottom);
  if (nearest === toLeft) return { x: bbox.x1, y: point.y };
  if (nearest === toRight) return { x: bbox.x2, y: point.y };
  if (nearest === toTop) return { x: point.x, y: bbox.y1 };
  return { x: point.x, y: bbox.y2 };
}

const boxArea = (bbox: BoundingBox): number => Math.abs(bbox.x2 - bbox.x1) * Math.abs(bbox.y2 - bbox.y1);

/** The nearest point on a polyline to `point`: the projection onto its nearest segment. */
function nearestPointOnPolyline(point: Point, polyline: number[]): Point {
  let best: Point = { x: polyline[0], y: polyline[1] };
  let bestDistance = Infinity;
  for (let i = 0; i + 3 < polyline.length; i += 2) {
    const start = { x: polyline[i], y: polyline[i + 1] };
    const end = { x: polyline[i + 2], y: polyline[i + 3] };
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const lengthSq = dx * dx + dy * dy;
    const t = lengthSq === 0 ? 0 : Math.max(0, Math.min(1, ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSq));
    const candidate = { x: start.x + t * dx, y: start.y + t * dy };
    const distance = Math.hypot(point.x - candidate.x, point.y - candidate.y);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = candidate;
    }
  }
  return best;
}

/**
 * The marked entity under a click in line drawing, with its snap point, or `null` for a
 * click on the paper.
 *
 * The margin is `BIND_MARGIN_SCREEN_PX / scale` image px, so it is the same width on
 * screen at every zoom. A box (equipment `bbox`, every instrument region, a duct) is a
 * candidate when the point is inside it or within the margin of it. A pipeline is a
 * candidate when the point is within the margin of its polyline. A box beats a
 * polyline. Among boxes the smallest wins, so a valve inside a vessel is reachable
 * (Q18). Among polylines the nearest wins.
 *
 * DECISION(2026-10-22, abhishek): A vertex binds to the marked equipment, instrument
 * and line geometries already on the sheet, and to nothing else (D1).
 * The click-target stacks are unmerged and edit the same files, and the marked boxes
 * exist on every sheet. SMEs who report lines whose ends sit on an unmarked symbol and
 * cannot bind are the signal to add a fourth candidate list.
 */
export function entityAtPoint(point: Point, scale: number, targets: BindTargets): EntityHit | null {
  const margin = BIND_MARGIN_SCREEN_PX / scale;

  let bestBox: { hit: EntityHit; area: number } | null = null;
  const considerBox = (kind: EntityHit['kind'], id: string, bbox: BoundingBox | null | undefined) => {
    if (!bbox || pointToBboxDistance(point, bbox) > margin) return;
    const area = boxArea(bbox);
    if (bestBox && area >= bestBox.area) return;
    bestBox = { hit: { kind, id, snap: snapToBoxEdge(point, bbox), outline: { kind: 'box', bbox } }, area };
  };
  for (const equipment of targets.equipments) considerBox('equipment', equipment.id, equipment.bbox);
  for (const instrument of targets.instruments) {
    for (const region of instrumentRegions(instrument)) considerBox('instrument', instrument.id, region);
  }
  for (const node of targets.nodes) considerBox('node', node.id, node.duct_bbox);
  if (bestBox) return (bestBox as { hit: EntityHit }).hit;

  let bestLine: { hit: EntityHit; distance: number } | null = null;
  for (const node of targets.nodes) {
    if (!node.polyline || node.polyline.length < 4) continue;
    const distance = pointToPolylineDistance(point, node.polyline);
    if (distance > margin || (bestLine && distance >= bestLine.distance)) continue;
    bestLine = {
      hit: { kind: 'node', id: node.id, snap: nearestPointOnPolyline(point, node.polyline), outline: { kind: 'polyline', points: node.polyline } },
      distance,
    };
  }
  return bestLine ? (bestLine as { hit: EntityHit }).hit : null;
}

function degreesToRadians(deg: number): number {
  return (deg * Math.PI) / 180;
}

function segmentLength(seg: Pick<LineSegment, 'x1' | 'y1' | 'x2' | 'y2'>): number {
  return Math.hypot(seg.x2 - seg.x1, seg.y2 - seg.y1);
}

function segmentAngleRad(seg: Pick<LineSegment, 'x1' | 'y1' | 'x2' | 'y2'>): number {
  // Treat direction as irrelevant: map angle to [0, PI).
  const a = Math.atan2(seg.y2 - seg.y1, seg.x2 - seg.x1);
  return a < 0 ? a + Math.PI : a;
}

function angleDiffRad(a: number, b: number): number {
  // Both in [0, PI). Smallest diff on circle of PI.
  const d = Math.abs(a - b);
  return Math.min(d, Math.PI - d);
}

function distancePointToInfiniteLine(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const denom = dx * dx + dy * dy;
  if (denom === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  const t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / denom;
  const projX = a.x + t * dx;
  const projY = a.y + t * dy;
  return Math.hypot(p.x - projX, p.y - projY);
}

function segmentMidpoint(seg: Pick<LineSegment, 'x1' | 'y1' | 'x2' | 'y2'>): Point {
  return { x: (seg.x1 + seg.x2) / 2, y: (seg.y1 + seg.y2) / 2 };
}

function unitDirection(seg: Pick<LineSegment, 'x1' | 'y1' | 'x2' | 'y2'>): Point {
  const dx = seg.x2 - seg.x1;
  const dy = seg.y2 - seg.y1;
  const len = Math.hypot(dx, dy);
  if (len === 0) return { x: 1, y: 0 };
  return { x: dx / len, y: dy / len };
}

function projectIntervalOntoAxis(seg: Pick<LineSegment, 'x1' | 'y1' | 'x2' | 'y2'>, axis: Point): [number, number] {
  const p1 = seg.x1 * axis.x + seg.y1 * axis.y;
  const p2 = seg.x2 * axis.x + seg.y2 * axis.y;
  return p1 < p2 ? [p1, p2] : [p2, p1];
}

function overlapRatioOnAxis(
  a: Pick<LineSegment, 'x1' | 'y1' | 'x2' | 'y2'>,
  b: Pick<LineSegment, 'x1' | 'y1' | 'x2' | 'y2'>,
  axis: Point
): number {
  const [a0, a1] = projectIntervalOntoAxis(a, axis);
  const [b0, b1] = projectIntervalOntoAxis(b, axis);
  const inter = Math.max(0, Math.min(a1, b1) - Math.max(a0, b0));
  const lenA = Math.max(1e-9, a1 - a0);
  const lenB = Math.max(1e-9, b1 - b0);
  return inter / Math.min(lenA, lenB);
}

function endpointDistanceMin(
  a: Pick<LineSegment, 'x1' | 'y1' | 'x2' | 'y2'>,
  b: Pick<LineSegment, 'x1' | 'y1' | 'x2' | 'y2'>
): number {
  const a1 = { x: a.x1, y: a.y1 };
  const a2 = { x: a.x2, y: a.y2 };
  const b1 = { x: b.x1, y: b.y1 };
  const b2 = { x: b.x2, y: b.y2 };
  const d11 = Math.hypot(a1.x - b1.x, a1.y - b1.y);
  const d12 = Math.hypot(a1.x - b2.x, a1.y - b2.y);
  const d21 = Math.hypot(a2.x - b1.x, a2.y - b1.y);
  const d22 = Math.hypot(a2.x - b2.x, a2.y - b2.y);
  return Math.min(d11, d12, d21, d22);
}

class UnionFind {
  private parent: number[];
  private rank: number[];

  constructor(n: number) {
    this.parent = Array.from({ length: n }, (_, i) => i);
    this.rank = Array.from({ length: n }, () => 0);
  }

  find(x: number): number {
    if (this.parent[x] !== x) this.parent[x] = this.find(this.parent[x]);
    return this.parent[x];
  }

  union(a: number, b: number): void {
    const ra = this.find(a);
    const rb = this.find(b);
    if (ra === rb) return;
    if (this.rank[ra] < this.rank[rb]) {
      this.parent[ra] = rb;
    } else if (this.rank[ra] > this.rank[rb]) {
      this.parent[rb] = ra;
    } else {
      this.parent[rb] = ra;
      this.rank[ra] += 1;
    }
  }

  groups(): Map<number, number[]> {
    const m = new Map<number, number[]>();
    for (let i = 0; i < this.parent.length; i++) {
      const r = this.find(i);
      const arr = m.get(r);
      if (arr) arr.push(i);
      else m.set(r, [i]);
    }
    return m;
  }
}

function pcaMergeCluster(cluster: LineSegment[]): Omit<MergedLineSegment, 'id' | 'source_segment_ids'> {
  const points: Array<{ x: number; y: number; w: number }> = [];
  for (const s of cluster) {
    const w = Math.max(0.0001, s.confidence ?? 1);
    points.push({ x: s.x1, y: s.y1, w });
    points.push({ x: s.x2, y: s.y2, w });
  }

  // Weighted centroid
  let sumW = 0;
  let meanX = 0;
  let meanY = 0;
  for (const p of points) {
    sumW += p.w;
    meanX += p.w * p.x;
    meanY += p.w * p.y;
  }
  meanX /= sumW;
  meanY /= sumW;

  // Weighted covariance
  let a = 0;
  let b = 0;
  let c = 0;
  for (const p of points) {
    const dx = p.x - meanX;
    const dy = p.y - meanY;
    a += p.w * dx * dx;
    b += p.w * dx * dy;
    c += p.w * dy * dy;
  }
  a /= sumW;
  b /= sumW;
  c /= sumW;

  // Principal axis from covariance eigenvector (2x2)
  let axis: Point;
  if (Math.abs(b) < 1e-12) {
    axis = a >= c ? { x: 1, y: 0 } : { x: 0, y: 1 };
  } else {
    const theta = 0.5 * Math.atan2(2 * b, a - c);
    axis = { x: Math.cos(theta), y: Math.sin(theta) };
  }

  // Project points to get extents
  let tMin = Infinity;
  let tMax = -Infinity;
  for (const p of points) {
    const t = (p.x - meanX) * axis.x + (p.y - meanY) * axis.y;
    tMin = Math.min(tMin, t);
    tMax = Math.max(tMax, t);
  }

  const x1 = meanX + axis.x * tMin;
  const y1 = meanY + axis.y * tMin;
  const x2 = meanX + axis.x * tMax;
  const y2 = meanY + axis.y * tMax;

  // Carry kind as the most frequent (tie-break: first)
  const kindCounts = new Map<string, number>();
  for (const s of cluster) {
    if (!s.kind) continue;
    kindCounts.set(s.kind, (kindCounts.get(s.kind) ?? 0) + 1);
  }
  let kind: string | undefined;
  let best = 0;
  for (const [k, n] of kindCounts.entries()) {
    if (n > best) {
      best = n;
      kind = k;
    }
  }

  const mergedConfidenceMax = cluster.reduce((mx, s) => Math.max(mx, s.confidence ?? -Infinity), -Infinity);

  return {
    x1,
    y1,
    x2,
    y2,
    kind,
    confidence: Number.isFinite(mergedConfidenceMax) ? mergedConfidenceMax : undefined,
    merged_count: cluster.length,
    merged_confidence_max: Number.isFinite(mergedConfidenceMax) ? mergedConfidenceMax : undefined,
  };
}

function stableMergedId(sourceIds: string[]): string {
  // Prefer numeric ordering for IDs like "LSEG-00123", else lexicographic.
  const parsed = sourceIds
    .map((id) => {
      const m = id.match(/(\d+)(?!.*\d)/);
      return { id, n: m ? Number(m[1]) : null };
    })
    .sort((a, b) => {
      if (a.n !== null && b.n !== null && a.n !== b.n) return a.n - b.n;
      if (a.n !== null && b.n === null) return -1;
      if (a.n === null && b.n !== null) return 1;
      return a.id.localeCompare(b.id);
    });
  return parsed[0]?.id ?? sourceIds[0] ?? 'merged';
}

/**
 * Merge overlapping/duplicated AI-extracted line segments into distinct segments for display.
 *
 * This is intended to reduce noise where the AI returns multiple nearly-identical segments
 * stacked on top of each other for the same physical process line.
 *
 * Approach:
 * - Build a similarity graph: segments are linked if they are (1) nearly parallel, (2) close in
 *   perpendicular distance, and (3) overlap substantially along their axis (or have very close endpoints).
 * - Use union-find to form clusters.
 * - For each cluster, compute a statistically merged representative using weighted PCA over endpoints.
 */
export function mergeOverlappingLineSegments(
  segments: LineSegment[],
  options: MergeLineSegmentsOptions = {}
): MergedLineSegment[] {
  const {
    angleThresholdDeg = 6,
    distanceThresholdPx = 6,
    overlapThreshold = 0.7,
    endpointDistancePx = 10,
    minLengthPx = 3,
  } = options;

  if (segments.length <= 1) {
    return segments.map((s) => ({
      ...s,
      source_segment_ids: [s.id],
      merged_count: 1,
      merged_confidence_max: s.confidence,
    }));
  }

  // Precompute metrics
  const segInfo = segments.map((s) => ({
    seg: s,
    len: segmentLength(s),
    ang: segmentAngleRad(s),
    mid: segmentMidpoint(s),
    axis: unitDirection(s),
  }));

  const uf = new UnionFind(segments.length);
  const angleThreshold = degreesToRadians(angleThresholdDeg);

  for (let i = 0; i < segInfo.length; i++) {
    const si = segInfo[i];
    if (si.len < minLengthPx) continue;

    for (let j = i + 1; j < segInfo.length; j++) {
      const sj = segInfo[j];
      if (sj.len < minLengthPx) continue;

      if (angleDiffRad(si.ang, sj.ang) > angleThreshold) continue;

      const lineDist = (distancePointToInfiniteLine(sj.mid, { x: si.seg.x1, y: si.seg.y1 }, { x: si.seg.x2, y: si.seg.y2 }) +
        distancePointToInfiniteLine(si.mid, { x: sj.seg.x1, y: sj.seg.y1 }, { x: sj.seg.x2, y: sj.seg.y2 })) / 2;
      if (lineDist > distanceThresholdPx) continue;

      const ov = overlapRatioOnAxis(si.seg, sj.seg, si.axis);
      const endDist = endpointDistanceMin(si.seg, sj.seg);
      if (ov >= overlapThreshold || endDist <= endpointDistancePx) {
        uf.union(i, j);
      }
    }
  }

  const merged: MergedLineSegment[] = [];
  for (const indices of uf.groups().values()) {
    const cluster = indices.map((idx) => segments[idx]);
    if (cluster.length === 1) {
      const s = cluster[0];
      merged.push({
        ...s,
        source_segment_ids: [s.id],
        merged_count: 1,
        merged_confidence_max: s.confidence,
      });
      continue;
    }

    const sourceIds = cluster.map((s) => s.id);
    const repr = pcaMergeCluster(cluster);
    merged.push({
      id: stableMergedId(sourceIds),
      ...repr,
      source_segment_ids: sourceIds,
    });
  }

  // Stable order: longer first (helps UI scan), then id.
  merged.sort((a, b) => {
    const la = segmentLength(a);
    const lb = segmentLength(b);
    if (lb !== la) return lb - la;
    return a.id.localeCompare(b.id);
  });

  return merged;
}
